import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { chatLogger } from '@/utils/logger';
import { performanceMonitor } from '@/utils/performance';
import { saveMessage, updateMessageInDb } from '@/services/chatDbService';

/**
 * Hook for managing chat messages
 * Handles message creation, updates, and streaming state
 */

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  metadata?: {
    questionId?: string;
    questionType?: string;
    questionText?: string;
    questionOptions?: Array<{ label: string; value: string; }>;
    chatbot_id?: string;
    shortname?: string;
    errormessage?: string;
    responsevalidation?: string;
    validation_rules?: any;
    validationType?: string;
    created_at?: string;
    updated_at?: string;
    placeholder?: string;
    order?: number;
    [key: string]: any;
  };
}

export interface MessageSystemManager {
  // State
  messages: ChatMessage[];
  isTyping: boolean;
  streamingMessageIds: Record<string, boolean>;

  // Actions
  addMessage: (text: string, sender: 'user' | 'bot', metadata?: ChatMessage['metadata']) => Promise<string>;
  updateMessage: (id: string, text: string) => void;
  setIsTyping: (typing: boolean) => void;
  setMessageStreaming: (id: string, isStreaming: boolean) => void;
  clearMessages: () => void;
  setConversationId: (conversationId: string | null) => void;

  // Utilities
  getMessageById: (id: string) => ChatMessage | undefined;
  getLastBotMessage: () => ChatMessage | undefined;
  getMessageCount: () => number;
}

export function useMessageSystem(): MessageSystemManager {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessageIds, setStreamingMessageIds] = useState<Record<string, boolean>>({});

  // Performance tracking
  const messageCountRef = useRef(0);
  // Track conversation ID for database saving
  const conversationIdRef = useRef<string | null>(null);

  const addMessage = useCallback(async (text: string, sender: 'user' | 'bot', metadata?: ChatMessage['metadata']): Promise<string> => {
    const startTime = performance.now();
    const messageId = uuidv4();

    const newMessage: ChatMessage = {
      id: messageId,
      text,
      sender,
      timestamp: new Date(),
      metadata
    };

    setMessages(prev => {
      const updated = [...prev, newMessage];

      // Performance monitoring
      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric('message-add', duration, 'ms', 'render');
      performanceMonitor.recordMetric('total-messages', updated.length, 'count', 'memory');

      chatLogger.message('Message added', {
        messageId,
        sender,
        textLength: text.length,
        totalMessages: updated.length,
        renderTime: duration
      });

      return updated;
    });

    // Save to database if we have a conversation ID
    if (conversationIdRef.current && text.trim()) {
      try {
        const success = await saveMessage(
          conversationIdRef.current,
          text,
          sender === 'bot',
          metadata?.questionId || undefined
        );

        if (success) {
          chatLogger.message('Message saved to database', {
            messageId,
            conversationId: conversationIdRef.current,
            sender
          });
        } else {
          chatLogger.error('Failed to save message to database', {
            messageId,
            conversationId: conversationIdRef.current,
            sender
          });
        }
      } catch (error) {
        chatLogger.error('Error saving message to database', {
          error,
          messageId,
          conversationId: conversationIdRef.current,
          sender
        });
      }
    }

    messageCountRef.current++;
    return messageId;
  }, []);

  const updateMessage = useCallback(async (id: string, text: string, saveToDb: boolean = false, explicitIsBot?: boolean) => {
    const startTime = performance.now();

    setMessages(prev => {
      const updated = prev.map(msg =>
        msg.id === id ? { ...msg, text } : msg
      );

      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric('message-update', duration, 'ms', 'render');

      chatLogger.message('Message updated', {
        messageId: id,
        textLength: text.length,
        renderTime: duration,
        saveToDb
      });

      return updated;
    });

    // Save to database if requested and we have a conversation ID
    if (saveToDb && conversationIdRef.current && text.trim()) {
      try {
        // Find the message to get its metadata
        const message = messages.find(msg => msg.id === id);

        // Use explicit isBot parameter if provided, otherwise infer from message
        let isBot: boolean;

        if (explicitIsBot !== undefined) {
          // Use the explicitly provided value
          isBot = explicitIsBot;
          console.log('✅ Using explicit isBot value:', {
            messageId: id,
            messageText: text.substring(0, 50),
            explicitIsBot,
            isBot
          });
        } else {
          // Fall back to inferring from message sender
          isBot = message?.sender === 'bot' || false;
          console.log('� Inferring isBot from message sender:', {
            messageId: id,
            messageText: text.substring(0, 50),
            messageSender: message?.sender,
            messageFound: !!message,
            isBot
          });
        }

        // Debug logging for message updates when explicit isBot is used
        if (saveToDb && explicitIsBot !== undefined) {
          console.log('✅ Message Update with explicit isBot:', {
            messageId: id,
            messageText: text.substring(0, 50),
            explicitIsBot,
            isBot
          });
        }

        const success = await updateMessageInDb(
          conversationIdRef.current,
          text,
          isBot || false,
          message?.metadata?.questionId || undefined
        );

        if (success) {
          chatLogger.message('Updated message saved to database', {
            messageId: id,
            conversationId: conversationIdRef.current,
            isBot
          });
        } else {
          chatLogger.error('Failed to save updated message to database', {
            messageId: id,
            conversationId: conversationIdRef.current,
            isBot
          });
        }
      } catch (error) {
        chatLogger.error('Error saving updated message to database', {
          error,
          messageId: id,
          conversationId: conversationIdRef.current
        });
      }
    }
  }, [messages]);

  const setMessageStreaming = useCallback((id: string, isStreaming: boolean) => {
    setStreamingMessageIds(prev => {
      if (isStreaming) {
        return { ...prev, [id]: true };
      } else {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
    });

    chatLogger.message('Message streaming state changed', {
      messageId: id,
      isStreaming,
      activeStreamingCount: Object.keys(streamingMessageIds).length
    });
  }, [streamingMessageIds]);

  const setConversationId = useCallback((conversationId: string | null) => {
    conversationIdRef.current = conversationId;
    chatLogger.message('Conversation ID set for message system', { conversationId });
  }, []);

  const clearMessages = useCallback(() => {
    chatLogger.message('Clearing all messages', {
      previousCount: messages.length
    });

    setMessages([]);
    setStreamingMessageIds({});
    setIsTyping(false);
    messageCountRef.current = 0;
    conversationIdRef.current = null; // Reset conversation ID
  }, [messages.length]);

  const getMessageById = useCallback((id: string): ChatMessage | undefined => {
    return messages.find(msg => msg.id === id);
  }, [messages]);

  const getLastBotMessage = useCallback((): ChatMessage | undefined => {
    const botMessages = messages.filter(msg => msg.sender === 'bot');
    return botMessages[botMessages.length - 1];
  }, [messages]);

  const getMessageCount = useCallback((): number => {
    return messages.length;
  }, [messages.length]);

  return {
    messages,
    isTyping,
    streamingMessageIds,
    addMessage,
    updateMessage,
    setIsTyping,
    setMessageStreaming,
    clearMessages,
    setConversationId,
    getMessageById,
    getLastBotMessage,
    getMessageCount
  };
}
